import { createContext, useContext, useState, useCallback } from 'react';

const FilterContext = createContext(null);

export const FilterProvider = ({ children }) => {
  const currentYear = new Date().getFullYear();

  const [filters, setFilters] = useState({
    yearFrom: new Date().getFullYear(),
    yearTo: new Date().getFullYear(),
    monthFrom: null,
    monthTo: null,
    aggregation: 'monthly',
    preset: 'current_year',
    initialized: false,
  });

  const [availableYears, setAvailableYears] = useState([]);

  const [comparisonPeriods, setComparisonPeriods] = useState({
    periodA: '',
    periodB: '',
  });

  const updateFilters = useCallback((updates) => {
    setFilters(prev => ({ ...prev, ...updates }));
  }, []);

  const applyPreset = useCallback((preset) => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;

    switch (preset) {
      case 'current_year':
        setFilters(prev => ({ ...prev, yearFrom: prev.latestYear || year, yearTo: prev.latestYear || year, monthFrom: null, monthTo: null, aggregation: 'monthly', preset }));
        break;
      case 'last_year':
        setFilters(prev => {
          const base = prev.latestYear || year;
          return { ...prev, yearFrom: base - 1, yearTo: base - 1, monthFrom: null, monthTo: null, aggregation: 'monthly', preset };
        });
        break;
      case 'last_12_months':
        setFilters(prev => ({ ...prev, yearFrom: year - 1, yearTo: year, monthFrom: null, monthTo: null, aggregation: 'monthly', preset }));
        break;
      case 'all':
      default:
        setFilters(prev => ({ ...prev, yearFrom: null, yearTo: null, monthFrom: null, monthTo: null, preset: 'all' }));
        break;
    }
  }, []);

  // Auto-detect latest year from data on first load
  const initializeYear = useCallback((years) => {
    if (years?.length) {
      setAvailableYears(prev => prev.length >= years.length ? prev : years);
      const latestYear = Math.max(...years);
      // Only update if yearFrom is different or not initialized
      setFilters(prev => {
        if (prev.yearFrom === latestYear && prev.initialized) return prev;
        return {
          ...prev,
          yearFrom: latestYear,
          yearTo: latestYear,
          latestYear,
          initialized: true,
        };
      });
    } else {
      // Fallback to current year only if not already initialized
      setFilters(prev => {
        if (prev.initialized) return prev;
        return { ...prev, yearFrom: currentYear, yearTo: currentYear, latestYear: currentYear, initialized: true };
      });
    }
  }, [currentYear]);

  const getQueryParams = useCallback(() => {
    const params = new URLSearchParams();
    if (filters.yearFrom) params.append('year_from', filters.yearFrom);
    if (filters.yearTo) params.append('year_to', filters.yearTo);
    if (filters.monthFrom) params.append('month_from', filters.monthFrom);
    if (filters.monthTo) params.append('month_to', filters.monthTo);
    if (filters.aggregation) params.append('aggregation', filters.aggregation);
    return params.toString();
  }, [filters]);

  return (
    <FilterContext.Provider value={{
      filters, updateFilters, applyPreset, getQueryParams,
      comparisonPeriods, setComparisonPeriods, initializeYear, availableYears,
    }}>
      {children}
    </FilterContext.Provider>
  );
};

export const useFilters = () => {
  const context = useContext(FilterContext);
  if (!context) throw new Error('useFilters must be used within FilterProvider');
  return context;
};
